import {
  ControlPosition,
  Map as GoogleMap,
  APIProvider as GoogleMapsJavaScriptAPIProvider,
  MapControl,
} from '@vis.gl/react-google-maps';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useMemo, useState } from 'react';

import {
  CenterControl,
  DropOffMarker,
  MapCameraSync,
  PickupMarker,
  RoutePolyline,
  VehicleMarker,
  ZoomControl,
} from './components';
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  MAP_STYLES,
  MAX_ZOOM,
  MIN_ZOOM,
} from './constants';
import { MapContainer, MapControlsContainer } from './Map.styles';
import { decodePolyline, sampleGpsCoordinates } from './utils';

const Map = ({
  center,
  origin,
  originAddress,
  destination,
  destinationAddress,
  pickupDateTime,
  dropOffDateTime,
  vehiclePosition = null,
  vehicle = null,
  disableControls = false,
  showBlueLine = true,
  directionsResult = null,
  mode = 'DRIVING',
  onLoad = null,
  lineColor,
  lineStyle,
  gpsCoordinates = [],
}) => {
  // Generate stable MAP_ID once per component instance to prevent unnecessary remounts
  const MAP_ID = useMemo(() => `map-${Date.now()}`, []);
  const defaultCenter = vehiclePosition || center || origin || DEFAULT_CENTER;
  const [paths, setPaths] = useState([]);

  /**
   * Direction handling - decodes backend directions for rendering
   *
   * Backend returns encoded polyline (via REST API), which we decode here
   * Supports both formats:
   * - overview_polyline.points (from REST API - needs decoding)
   * - overview_path (from DirectionsService - already decoded)
   */
  useEffect(() => {
    if (!directionsResult) {
      setPaths([]);
      return;
    }

    const route = directionsResult?.routes?.[0];
    if (!route) {
      setPaths([]);
      return;
    }

    // Priority 1: Try to decode encoded polyline (from REST API)
    const encodedPolyline = route.overview_polyline?.points;
    if (encodedPolyline) {
      try {
        let pathsValue;

        // Try using Google Maps geometry library first (more accurate)
        if (window.google?.maps?.geometry?.encoding) {
          const decodedPath =
            window.google.maps.geometry.encoding.decodePath(encodedPolyline);
          pathsValue = decodedPath.map((latLng) => ({
            lat: latLng.lat(),
            lng: latLng.lng(),
          }));
        } else {
          // Fallback to manual decoder
          pathsValue = decodePolyline(encodedPolyline);
        }

        setPaths(pathsValue);
        return;
      } catch (error) {
        // Failed to decode - continue to try other methods
      }
    }

    // Priority 2: Check for already decoded overview_path (from DirectionsService)
    const overviewPath = route.overview_path;
    if (overviewPath) {
      const pathsValue = overviewPath
        .map((p) => {
          if (!p) return null;

          // LatLng object: use lat()/lng() methods
          if (typeof p.lat === 'function' && typeof p.lng === 'function') {
            return { lat: p.lat(), lng: p.lng() };
          }

          // Already a literal { lat, lng }
          return p;
        })
        .filter(Boolean);
      setPaths(pathsValue);
      return;
    }

    // Fallback: Draw straight line between origin and destination
    if (origin && destination) {
      setPaths([origin, destination]);
      return;
    }

    // No path data available
    setPaths([]);
  }, [directionsResult, origin, destination]);

  const sampledGpsCoordinates = useMemo(() => {
    if (!gpsCoordinates || gpsCoordinates.length === 0) return [];
    const sampled = sampleGpsCoordinates(gpsCoordinates, 200);
    return sampled.map((coord) => ({
      lat: coord.position.longLat[1],
      lng: coord.position.longLat[0],
    }));
  }, [gpsCoordinates]);

  const coordinatesToDisplay = useMemo(() => {
    if (sampledGpsCoordinates.length > 0) return sampledGpsCoordinates;
    if (paths && paths.length > 0) return paths;
    if (origin && destination) return [origin, destination];
    return [];
  }, [sampledGpsCoordinates, paths, origin, destination]);

  return (
    <MapContainer>
      <GoogleMapsJavaScriptAPIProvider
        apiKey={process.env.GOOGLE_MAPS_API_KEY}
        onLoad={() => {
          // Call the onLoad callback if provided
          if (onLoad) {
            onLoad();
          }
        }}
        mapContainerStyle={{
          width: '100%',
          height: '100%',
        }}
        options={{
          mapTypeControl: false,
          scaleControl: true,
          streetViewControl: false,
          disableDefaultUI: true,
          scrollwheel: !disableControls,
          styles: MAP_STYLES,
        }}
      >
        <GoogleMap
          mapId={MAP_ID}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          defaultZoom={DEFAULT_ZOOM}
          defaultCenter={defaultCenter}
          disableDefaultUI
          gestureHandling={disableControls ? 'none' : 'auto'}
        >
          <MapCameraSync center={defaultCenter} zoom={DEFAULT_ZOOM} />
          <MapControl position={ControlPosition.BLOCK_START_INLINE_END}>
            <MapControlsContainer>
              <ZoomControl minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} />
              <CenterControl
                center={defaultCenter}
                defaultZoom={DEFAULT_ZOOM}
                paths={coordinatesToDisplay}
              />
            </MapControlsContainer>
          </MapControl>
          {showBlueLine && coordinatesToDisplay.length > 0 && (
            <RoutePolyline
              path={coordinatesToDisplay}
              strokeColor={lineColor}
              lineStyle={lineStyle}
              mode={mode}
            />
          )}
          <PickupMarker
            position={origin}
            formattedAddress={originAddress}
            pickupDateTime={pickupDateTime}
          />
          <DropOffMarker
            position={destination}
            formattedAddress={destinationAddress}
            dropOffDateTime={dropOffDateTime}
          />
          {vehiclePosition && (
            <VehicleMarker
              position={vehiclePosition}
              make={vehicle?.make}
              model={vehicle?.model}
              plateState={vehicle?.plateState}
              plate={vehicle?.plate}
              color={vehicle?.color}
            />
          )}
        </GoogleMap>
      </GoogleMapsJavaScriptAPIProvider>
    </MapContainer>
  );
};

Map.propTypes = {
  center: PropTypes.object,
  origin: PropTypes.object,
  originAddress: PropTypes.string,
  destination: PropTypes.object,
  destinationAddress: PropTypes.string,
  pickupDateTime: PropTypes.string,
  dropOffDateTime: PropTypes.string,
  vehiclePosition: PropTypes.object,
  vehicle: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    plateState: PropTypes.string,
    plate: PropTypes.string,
    color: PropTypes.string,
  }),
  disableControls: PropTypes.bool,
  showBlueLine: PropTypes.bool,
  directionsResult: PropTypes.object,
  mode: PropTypes.oneOf(['DRIVING', 'TRANSIT']),
  onLoad: PropTypes.func,
  lineColor: PropTypes.string,
  lineStyle: PropTypes.string,
  gpsCoordinates: PropTypes.array,
};

function areEqual(prevProps, nextProps) {
  // Only compare props that actually affect rendering
  return (
    isEqual(prevProps.origin, nextProps.origin) &&
    isEqual(prevProps.destination, nextProps.destination) &&
    isEqual(prevProps.originAddress, nextProps.originAddress) &&
    isEqual(prevProps.destinationAddress, nextProps.destinationAddress) &&
    isEqual(prevProps.pickupDateTime, nextProps.pickupDateTime) &&
    isEqual(prevProps.dropOffDateTime, nextProps.dropOffDateTime) &&
    isEqual(prevProps.vehiclePosition, nextProps.vehiclePosition) &&
    isEqual(prevProps.vehicle, nextProps.vehicle) &&
    isEqual(prevProps.directionsResult, nextProps.directionsResult) &&
    prevProps.mode === nextProps.mode &&
    prevProps.showBlueLine === nextProps.showBlueLine &&
    prevProps.lineColor === nextProps.lineColor &&
    prevProps.lineStyle === nextProps.lineStyle &&
    isEqual(prevProps.gpsCoordinates, nextProps.gpsCoordinates)
  );
}

export default memo(Map, areEqual);
